// src/components/JurySeatProgress.jsx
// Visual "jury box" showing seats filling up as votes come in
// Props: filled, total, label?, compact?, className?, showLabel?

import React, { useEffect, useRef } from 'react';

// Court-themed copy for different states
const FILLING_MESSAGES = [
  '👨‍⚖️ Jury filing in…',
  '🧑‍⚖️ Taking their seats…',
  '⚖️ Assembling the jury…',
];

const DELIBERATING_MESSAGES = [
  '⚖️ Deliberating…',
  '🧑‍⚖️ Foreperson is counting votes…',
  '📜 Reviewing the evidence…',
  '🤔 Jury is conferring…',
];

const READY_MESSAGES = [
  '✅ Jury is ready!',
  '📋 All seats filled!',
  '⚖️ Verdict unlocked!',
];

function getStatusMessage(filled, total) {
  if (filled >= total) {
    return READY_MESSAGES[Math.floor(Math.random() * READY_MESSAGES.length)];
  }
  if (filled >= total * 0.7) {
    return DELIBERATING_MESSAGES[Math.floor(Math.random() * DELIBERATING_MESSAGES.length)];
  }
  return FILLING_MESSAGES[Math.floor(Math.random() * FILLING_MESSAGES.length)];
}

export default function JurySeatProgress({
  filled = 0,
  total = 6,
  label,
  compact = false,
  className = '',
  showLabel = true,
  onVisible,
  trialId,
}) {
  const containerRef = useRef(null);
  const hasTracked = useRef(false);

  // Track when component becomes visible (once per mount)
  useEffect(() => {
    if (hasTracked.current || !onVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasTracked.current) {
          hasTracked.current = true;
          onVisible({ trialId, filled, total });
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [onVisible, trialId, filled, total]);

  // Ensure we have valid numbers
  const safeTotal = Math.max(1, Number(total) || 6);
  const safeFilled = Math.min(safeTotal, Math.max(0, Number(filled) || 0));
  const percentage = Math.round((safeFilled / safeTotal) * 100);
  const compactSeatCap = 6;
  const visualTotal = compact ? Math.min(safeTotal, compactSeatCap) : safeTotal;
  const visualFilled = compact && safeTotal > compactSeatCap
    ? Math.min(
        visualTotal,
        Math.max(0, Math.round((safeFilled / safeTotal) * visualTotal))
      )
    : Math.min(visualTotal, safeFilled);
  const compactSeatClass = safeTotal > compactSeatCap ? 'w-3 h-3' : 'w-4 h-4';

  // Generate seat elements
  const seats = [];
  for (let i = 0; i < visualTotal; i++) {
    const isFilled = i < visualFilled;
    seats.push(
      <div
        key={i}
        className={`
          ${compact ? compactSeatClass : 'w-6 h-6'}
          rounded-full
          transition-all duration-300 ease-out
          ${isFilled
            ? 'bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg shadow-purple-500/30 scale-100'
            : 'bg-slate-700 border border-slate-600 scale-90 opacity-60'
          }
        `}
        style={{
          transitionDelay: isFilled ? `${i * 50}ms` : '0ms',
        }}
      />
    );
  }

  // Determine status message
  const statusMessage = label || getStatusMessage(safeFilled, safeTotal);

  if (compact) {
    // Compact inline version
    return (
      <div ref={containerRef} className={`flex min-w-0 max-w-full items-center gap-2 ${className}`}>
        <div className="flex shrink-0 items-center gap-1 overflow-hidden">
          {seats}
        </div>
        {safeTotal > compactSeatCap ? (
          <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">
            +{safeTotal - compactSeatCap}
          </span>
        ) : null}
        <span className="min-w-0 text-xs text-slate-400 whitespace-nowrap">
          {safeFilled}/{safeTotal}
        </span>
      </div>
    );
  }

  // Full version with label and progress
  return (
    <div ref={containerRef} className={`${className}`}>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-200">{statusMessage}</span>
          <span className="text-sm text-slate-400">{safeFilled}/{safeTotal} seated</span>
        </div>
      )}

      {/* Seat icons */}
      <div className="flex justify-center gap-2 mb-3">
        {seats}
      </div>

      {/* Progress bar underneath */}
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Status text */}
      <div className="mt-2 text-center text-xs text-slate-400">
        {safeFilled >= safeTotal
          ? 'Verdict ready!'
          : `${safeTotal - safeFilled} more vote${safeTotal - safeFilled === 1 ? '' : 's'} needed`}
      </div>
    </div>
  );
}

// Export the status message getter for use elsewhere
export { getStatusMessage, FILLING_MESSAGES, DELIBERATING_MESSAGES, READY_MESSAGES };
